// Date rules of the orders CSV export. The backend enforces the same limits (orderExportHelper.js in vsa-backend).

/** An export covers at most this many calendar days, counting both the first and the last day. */
export const MAX_EXPORT_RANGE_DAYS = 90;

/** The "Last N days" shortcuts offered in the dialog. */
export const EXPORT_PRESET_DAYS = [30, 60, 90];

export interface ExportDateRange {
  start_date: string; // yyyy-MM-dd
  end_date: string; // yyyy-MM-dd, included
}

const DAY_MS = 24 * 60 * 60 * 1000;
const pad = (n: number): string => String(n).padStart(2, '0');

/** A local calendar date as yyyy-MM-dd (toISOString would shift it to UTC and can land on the wrong day). */
export function toDateString(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// yyyy-MM-dd -> that day at 00:00 UTC (UTC so daylight saving can't change a day's length), null if not a real date
function parseDateString(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  const isRealDate =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
  return isRealDate ? date : null;
}

/** Days covered by the range, both ends included (Jan 1 to Jan 1 is 1 day); null if either date is invalid. */
export function countDays(start: string, end: string): number | null {
  const startDate = parseDateString(start);
  const endDate = parseDateString(end);
  if (!startDate || !endDate) {
    return null;
  }

  return Math.round((endDate.getTime() - startDate.getTime()) / DAY_MS) + 1;
}

/** "Last N days": today and the N - 1 days before it, so N days in total. */
export function getPresetRange(
  days: number,
  today: Date = new Date()
): ExportDateRange {
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - (days - 1)
  );
  return { start_date: toDateString(start), end_date: toDateString(today) };
}

/**
 * Checks a custom range. `range` is set only when it can be exported; `error` is the message to show the user
 * (null while a date is still missing, so the dialog doesn't complain before the user has picked both).
 */
export function checkCustomRange(
  start: string,
  end: string,
  today: string
): { range: ExportDateRange | null; error: string | null } {
  if (!start || !end) {
    return { range: null, error: null };
  }

  const days = countDays(start, end);
  if (days === null) {
    return { range: null, error: 'Please enter valid dates.' };
  }
  if (days < 1) {
    return { range: null, error: 'End date cannot be before start date.' };
  }
  if (end > today) {
    return { range: null, error: 'End date cannot be in the future.' };
  }
  if (days > MAX_EXPORT_RANGE_DAYS) {
    return {
      range: null,
      error: `The date range cannot be longer than ${MAX_EXPORT_RANGE_DAYS} days (you selected ${days} days).`,
    };
  }

  return { range: { start_date: start, end_date: end }, error: null };
}
